import * as React from 'react'

interface UseMobileDetectionProps {
  isMobileView: boolean
  setIsMobileView: (isMobile: boolean) => void
  showMobileConversationList: boolean
  setShowMobileConversationList: (show: boolean) => void
  setSelectedConversationId?: (id: number | undefined) => void
  setTargetUserName?: (name: string) => void
  resetChatState?: () => void
  selectedConversationId?: number
  markConversationAsRead?: (conversationId: number) => Promise<void>
  isCompactLayout?: boolean
}

export const useMobileDetection = ({
  isMobileView,
  setIsMobileView,
  showMobileConversationList,
  setShowMobileConversationList,
  setSelectedConversationId,
  setTargetUserName,
  resetChatState,
  markConversationAsRead: _markConversationAsRead,
  isCompactLayout,
}: UseMobileDetectionProps) => {
  const useCompact = isCompactLayout ?? isMobileView
  // Detect mobile view on mount and resize
  React.useEffect(() => {
    const checkMobileView = () => {
      const isMobile = window.innerWidth < 1024 // lg breakpoint
      setIsMobileView(isMobile)

      // If switching to mobile and we have a selected conversation, show messages
      if (isMobile && !showMobileConversationList) {
        setShowMobileConversationList(false)
      }
    }

    // Check on mount
    checkMobileView()

    // Add resize listener
    window.addEventListener('resize', checkMobileView)

    // Cleanup
    return () => window.removeEventListener('resize', checkMobileView)
  }, [setIsMobileView, setShowMobileConversationList, showMobileConversationList])

  // Function to go back to conversation list (compact layouts only)
  const goBackToConversations = React.useCallback(() => {
    if (useCompact) {
      setShowMobileConversationList(true)
      // Reset all chat state when going back to conversations
      if (resetChatState) {
        resetChatState()
      } else {
        // Fallback to individual resets if resetChatState is not provided
        if (setSelectedConversationId) {
          setSelectedConversationId(undefined)
        }
        if (setTargetUserName) {
          setTargetUserName('')
        }
      }
    }
  }, [useCompact, setShowMobileConversationList, setSelectedConversationId, setTargetUserName, resetChatState])

  // Function to go to messages (compact layouts only)
  const goToMessages = React.useCallback(() => {
    if (useCompact) {
      setShowMobileConversationList(false)
    }
  }, [useCompact, setShowMobileConversationList])

  return {
    goBackToConversations,
    goToMessages,
  }
}
